// Minimal flash.events surface: only the listener plumbing the game relies on.

export class Event {
  static readonly ENTER_FRAME = "enterFrame";
  static readonly DEACTIVATE = "deactivate";
  static readonly SOUND_COMPLETE = "soundComplete";

  constructor(public readonly type: string) {}
}

export class MouseEvent extends Event {
  static readonly CLICK = "click";
  static readonly MOUSE_DOWN = "mouseDown";
  static readonly MOUSE_UP = "mouseUp";
  static readonly MOUSE_MOVE = "mouseMove";

  constructor(
    type: string,
    public stageX = 0,
    public stageY = 0,
  ) {
    super(type);
  }
}

export class KeyboardEvent extends Event {
  static readonly KEY_DOWN = "keyDown";
  static readonly KEY_UP = "keyUp";

  constructor(
    type: string,
    public keyCode = 0,
  ) {
    super(type);
  }
}

type Listener = (event: Event) => void;

export class EventDispatcher {
  addEventListener(type: string, listener: Listener): void {
    this.listeners ??= new Map();
    const bucket = this.listeners.get(type);
    if (!bucket) {
      this.listeners.set(type, [listener]);
      let set = byType.get(type);
      if (!set) {
        set = new Set();
        byType.set(type, set);
      }
      set.add(this);
      return;
    }
    if (!bucket.includes(listener)) bucket.push(listener);
  }

  removeEventListener(type: string, listener: Listener): void {
    const bucket = this.listeners?.get(type);
    if (!bucket) return;
    const i = bucket.indexOf(listener);
    if (i >= 0) bucket.splice(i, 1);
    if (bucket.length === 0) {
      this.listeners!.delete(type);
      byType.get(type)?.delete(this);
    }
  }

  dispatchEvent(event: Event): void {
    const bucket = this.listeners?.get(event.type);
    if (!bucket || bucket.length === 0) return;
    // one listener is the norm; only a shared bucket needs the snapshot a
    // listener that unregisters itself mid-dispatch depends on.
    if (bucket.length === 1) {
      bucket[0](event);
      return;
    }
    for (const listener of bucket.slice()) listener(event);
  }

  private listeners: Map<string, Listener[]> | null = null;
}

export function broadcast(type: string): void {
  broadcastEvent(new Event(type));
}

/** Delivers a prebuilt event to every dispatcher listening for its type. */
export function broadcastEvent(event: Event): void {
  const set = byType.get(event.type);
  if (!set || set.size === 0) return;
  // Snapshot: a listener registered by this event's handlers (Game swapping
  // its own enterFrame handler) must not run until the next frame.
  for (const target of [...set]) target.dispatchEvent(event);
}

// Flash Player delivers enterFrame to every listening DisplayObject, including
// ones detached from the display list (SoundFX relies on this), so the frame
// loop drives a flat registry instead of walking the tree.
const byType = new Map<string, Set<EventDispatcher>>();
